/**
 * royaleConstants.ts
 *
 * Single source of truth for every tunable in Puzzle Royale — the N-player
 * Fun Challenge. The PRD requires all constants to be configurable, so they
 * live here (frontend) and are mirrored by `funPuzzleRoyale.getDefaultSettings()`
 * (cc-game-service) and the Django sequence/result defaults.
 *
 * Design / scoring physics are sourced from the authoritative design bundle:
 *   src/Screens/FunChallenges/docs/design/project/Puzzle Race - Live Strip.html
 *   src/Screens/FunChallenges/docs/design/chat1.md (intent transcript)
 *
 * Capacity model (v2): the ENGINE handles up to 100 players. 10 is only a
 * configurable PUBLIC / CLASSROOM display + auto-start cap, NOT an engine
 * limit — a privileged caller can raise maxPlayers up to ENGINE_MAX_PLAYERS.
 */

import type {PieceTier, RoyaleSettings} from './royaleTypes';

// ── Capacity ──────────────────────────────────────────────────────────────
/** Hard engine ceiling — store, broadcast, leaderboard all run this many. */
export const ENGINE_MAX_PLAYERS = 100;
/** Public + classroom entry cap. Configurable; NOT an engine limit. */
export const DISPLAY_CAP = 10;
/** Minimum joined players the host needs before the manual Start unlocks. */
export const MIN_TO_START = 2;
/** Lobby auto-starts the moment the joined count hits this (= the display cap). */
export const AUTO_START_AT = DISPLAY_CAP;
/** Race strip always shows this many top rows + a fixed "you" row. */
export const LEADERBOARD_VISIBLE = 10;
/** App (future) shows you + this many others (top 3 if you're rank 1/2). */
export const APP_VISIBLE_ROWS = 3;

// ── Lobby ───────────────────────────────────────────────────────────────────
/** A lobby expires this long after creation; late joiners are told to recreate. */
export const LOBBY_EXPIRY_MS = 15 * 60 * 1000;
/** Pre-game 3-2-1 countdown, in seconds (mirrors the server COUNTDOWN_SEC). */
export const COUNTDOWN_SEC = 3;
/**
 * How far BEFORE `scheduled_at` a tournament's start timer fires. Mirrors the
 * server's `TOURNAMENT_START_LEAD_MS` in funChallengeRoomProcessor.js — **keep
 * the two in sync or the lobby countdown drifts out of step with the race.**
 *
 * Was 10s (arena parity), which made the pill jump from "starts in 10" straight
 * into the race and started every tournament a full 10s early. Now 1s: just a
 * cushion for timer dispatch jitter, so the advertised time is effectively the
 * real start. The lobby countdown targets `scheduled_at - this`.
 */
export const TOURNAMENT_START_LEAD_MS = 1 * 1000;

/**
 * Post-game review: may a viewer inspect ANOTHER player's per-puzzle results
 * (which puzzles they solved / got wrong, and the board for each)?
 *
 * `true`  — tap any player in the final standings to load their puzzle grid.
 * `false` — the grid is locked to your OWN attempts. Other players still appear
 *           in the standings with their name, points, score and rank; only the
 *           per-puzzle breakdown is private.
 *
 * Off by default: in a competitive tournament, seeing exactly which puzzles a
 * rival missed is information they didn't consent to share. Flip to `true` for
 * classroom/coaching contexts where that visibility is the point.
 */
export const TOURNAMENT_REVIEW_SHOW_OTHERS = false;

// ── Time control (host slider) ────────────────────────────────────────────
/** Total race duration options, in seconds. Slider snaps to these. */
export const TIME_CHIPS = [30, 60, 90, 120, 180, 300] as const;
export const TIME_DEFAULT = 90;

// ── Topics (host) ─────────────────────────────────────────────────────────
/**
 * Topics are OPTIONAL — an empty selection means "no topic filter" (any
 * theme). When the host does pick, they may select up to this many. The
 * topic list itself lives in royaleTopics.ts (boolean theme columns).
 */
export const MAX_TOPICS = 4;

// ── Difficulty (host) ─────────────────────────────────────────────────────
/**
 * Two families of difficulty, both sent to the server as the same `difficulty`
 * string:
 *   - MOVE-COUNT bands (easy/medium/hard) — the original, available everywhere
 *     (standalone + classroom). Server bands by the `moves` string length.
 *   - RATING bands (f1..f4) — CLASSROOM ONLY. Server restricts to that rating
 *     range (dropping the usual rating>1000 floor) and caps solution length at
 *     ≤4 player moves; when the chosen topics run thin it backfills from any
 *     topic in the same band. Only the classroom coach-create UI ever emits
 *     these (gated by RoyaleSettings' `allowRatingBands` prop), so standalone /
 *     PTB / PDB behaviour is unchanged.
 */
export type MoveDifficulty = 'easy' | 'medium' | 'hard';
export type RatingBand = 'f1' | 'f2' | 'f3' | 'f4';
export type Difficulty = MoveDifficulty | RatingBand;
export const DIFFICULTY_OPTIONS: MoveDifficulty[] = ['easy', 'medium', 'hard'];
export const DIFFICULTY_DEFAULT: Difficulty = 'easy';
/**
 * Difficulty → player-move-count band (theme-independent; NOT mate columns).
 * Derived server-side from the `moves` string length: easy = 1-2 player moves,
 * medium = 3, hard = 4+. Kept here only as documentation for the UI labels.
 */
export const DIFFICULTY_MOVE_LABELS: Record<MoveDifficulty, string> = {
  easy: '1–2 moves',
  medium: '3 moves',
  hard: '4+ moves',
};

// ── Rating bands (classroom only) ──────────────────────────────────────────
/**
 * The F-levels a coach can pick in the classroom. Ranges match the server
 * (_ROYALE_RATING_BANDS) and overlap intentionally. All F-levels cap solution
 * length at ≤4 player moves. Order = display order in the difficulty picker.
 */
export const RATING_BAND_OPTIONS: RatingBand[] = ['f1', 'f2', 'f3', 'f4'];
export const RATING_BAND_LABELS: Record<RatingBand, string> = {
  f1: 'F1',
  f2: 'F2',
  f3: 'F3',
  f4: 'F4',
};
/** Sub-label under each F-level chip: the rating range it draws from. */
export const RATING_BAND_RANGE_LABELS: Record<RatingBand, string> = {
  f1: '400–900',
  f2: '800–1300',
  f3: '1100–1700',
  f4: '1500–2000',
};

// ── Demo-Call curated assessments (classroom, Demo Call classes only) ────────
/**
 * Fixed, curated puzzle SETS for Demo-Call classes (Demo_B / Demo_F1 / Demo_F2).
 * Unlike difficulty (which the server bands over cc_puzzles_global), an
 * assessment is a distinct settings field (`RoyaleSettings.assessment`) naming a
 * fixed set in cc_csoc_classroom_pr_puzzles. The coach picks ONE; it REPLACES
 * the move/rating difficulty chips and is shown ONLY when the classroom
 * batch_type == 'Demo Call' (RoyaleSettings' `allowDemoAssessments` prop). The
 * server (cc-game-service) routes a non-empty `assessment` to the curated
 * classroom_puzzle_royale_sequence endpoint. Keep these keys in sync with the
 * chess `_CLASSROOM_ASSESSMENTS` allowlist and the puzzle rows' assessment_name.
 */
export type DemoAssessment = 'demo_b' | 'demo_f1' | 'demo_f2' | 'demo_g1';
export const DEMO_ASSESSMENT_OPTIONS: DemoAssessment[] = [
  'demo_b',
  'demo_f1',
  'demo_f2',
  'demo_g1',
];
export const DEMO_ASSESSMENT_LABELS: Record<DemoAssessment, string> = {
  demo_b: 'Demo B',
  demo_f1: 'Demo F1',
  demo_f2: 'Demo F2',
  demo_g1: 'Demo G1',
};
/** Sub-label under each demo chip: a short description of the set's level. */
export const DEMO_ASSESSMENT_RANGE_LABELS: Record<DemoAssessment, string> = {
  demo_b: 'Beginner',
  demo_f1: 'Foundation 1',
  demo_f2: 'Foundation 2',
  demo_g1: 'General 1',
};

/**
 * Puzzle-id offset for classroom curated (Demo-Call) puzzles. The server serves
 * their ids as (row id + this), so the client can tell a curated puzzle from a
 * cc_puzzles_global one by `id >= DEMO_ID_OFFSET`. Kept in sync with the Django
 * DEMO_ID_OFFSET (fun_challenges/views.py, app/views_learning_puzzles.py).
 */
export const DEMO_ID_OFFSET = 1000000000;

// ── Punish mistakes (host) ────────────────────────────────────────────────
export const PUNISH_DEFAULT = false;
/** When punish is on, how many wrong puzzles eliminate a player from scoring. */
export const PUNISH_THRESHOLDS = [5, 10] as const;
export const PUNISH_DEFAULT_THRESHOLD = 5;
/**
 * SOLO punish thresholds. Solo adds a tighter 3-wrong option (3/5/10) on top of
 * the multiplayer 5/10 — a lone player wants a shorter mistake leash. Only the
 * solo settings UI uses this; multiplayer royale keeps PUNISH_THRESHOLDS.
 */
export const PUNISH_THRESHOLDS_SOLO = [3, 5, 10] as const;

// ── Solo Puzzle Royale ──────────────────────────────────────────────────────
/**
 * SOLO is Puzzle Royale with N=1. It shows NO difficulty control — instead the
 * server always serves a ramping RATING ladder (harder puzzles as you advance),
 * with every puzzle capped at <= 4 PLAYER moves (plies <= 8). This constant is
 * the CLIENT MIRROR of the server's _SOLO_LADDER (chess fun_challenges/views.py)
 * — kept here for display / documentation; the server is authoritative for the
 * actual sequence. Non-overlapping rating bands (a true difficulty ramp); the
 * `<300` tier from the original spec is DROPPED because the puzzle pool has no
 * puzzles rated below 399 (verified against prod, 2026-07-17). `count` puzzles
 * are drawn per tier; the final tier (max=null) fills the rest of the sequence.
 */
export const SOLO_LADDER: Array<{minRating: number | null; maxRating: number | null; count: number | null}> = [
  {minRating: null, maxRating: 500, count: 3},        // tier 1: rating < 500
  {minRating: 500, maxRating: 700, count: 3},          // tier 2: 500–700
  {minRating: 700, maxRating: 1100, count: 3},         // tier 3: 700–1100
  {minRating: 1100, maxRating: 1700, count: null},     // tier 4: 1100–1700, fills the rest
];
/** Every solo puzzle is capped at this many PLAYER moves (plies <= 2*this). */
export const SOLO_MAX_PLAYER_MOVES = 4;
/** Solo never serves a puzzle rated above this (top of the ladder's final tier). */
export const SOLO_MAX_RATING = 1700;

// ── Scoring: streak ladder ──────────────────────────────────────────────────
/**
 * Streak bonus TIERS. A correct solve is always +1 point; PLUS a per-solve
 * bonus equal to the highest tier whose threshold ≤ the current combo — applied
 * on EVERY consecutive solve while the streak is up, not just once at the
 * threshold (PRD update 2026-06-14). A wrong solve resets the combo to 0 (no
 * point). Tiers (combo ≥ `at` → `bonus` per solve):
 *   combo 3–4 → +1, 5–9 → +2, 10–14 → +3, 15+ → +5.
 * Consumed by royaleScoring.comboTierBonus (client) + recordRoyaleScore (server).
 */
export const STREAK_LADDER: Array<{at: number; bonus: number}> = [
  {at: 3, bonus: 1},
  {at: 5, bonus: 2},
  {at: 10, bonus: 3},
  {at: 15, bonus: 5},
];

// ── Piece rank (points → piece) ─────────────────────────────────────────────
/**
 * Point bands → piece tier. King is NOT a band here — it's the LEADER badge:
 * the current points leader's moving piece renders as a King once any player
 * reaches KING_ENABLED_AT, transferring live as the lead changes (see
 * royaleScoring.resolveKing). PRD bands: Pawn 0-2 / Knight 3-5 / Bishop 6-9
 * / Rook 10-14 / Queen 15+.
 */
export const PIECE_BANDS: Array<{tier: PieceTier; min: number}> = [
  {tier: 'pawn', min: 0},
  {tier: 'knight', min: 3},
  {tier: 'bishop', min: 6},
  {tier: 'rook', min: 10},
  {tier: 'queen', min: 15},
];
/** King (leader badge) only lights up once SOME player has reached this. */
export const KING_ENABLED_AT = 3;

// ── Player colours ──────────────────────────────────────────────────────────
/**
 * When true, each of the (up to DISPLAY_CAP) players gets a DISTINCT colour
 * assigned by lobby JOIN ORDER, and the local user ("you") is ALWAYS green so
 * a player consistently sees themselves in green. When false, fall back to the
 * legacy per-userId hash (colorForUser) — whatever was present before.
 */
export const DIFFERENT_COLOR = true;
/** The colour the local user always renders as when DIFFERENT_COLOR is on. */
export const SELF_COLOR = '#1A7E73'; // green
/**
 * Distinct accents handed out by join order to the OTHER players (green is
 * reserved for "you", so it's excluded here). Deeper colours, legible on the
 * cream/white surfaces (from the Live Strip design). Up to DISPLAY_CAP-1 others
 * get a unique one; beyond that they cycle.
 */
export const PLAYER_COLOR_SEQUENCE = [
  '#9A7600',
  '#C05828',
  '#6845B0',
  '#2878C0',
  '#B03888',
  '#985018',
  '#A81818',
  '#2858A8',
  '#1A7535',
];

// ── Race-strip physics (from the Live Strip design) ─────────────────────────
/** Leader sits here (%) until someone crosses LEADER_X_THRESHOLD points… */
export const LEADER_X_PRE = 50;
/** …then the leader pushes to here (%). */
export const LEADER_X_POST = 85;
/** Point total at which the track opens up (leader → LEADER_X_POST). */
export const LEADER_X_THRESHOLD = 3;
/** Base horizontal spread: % per point of gap behind the leader. */
export const X_SCALE_PER_PT = 2;
/** Last place never falls below this (%) — scale compresses to keep it visible. */
export const MIN_X = 5;

// ── Sequence ────────────────────────────────────────────────────────────────
/** Puzzles pre-rolled per race. Generous (5-min races can be ~100 puzzles). */
export const SEQUENCE_LENGTH = 100;

// ── In-race controls ────────────────────────────────────────────────────────
/**
 * Show a "Leave race" (forfeit) control during a live race. When true, a
 * player can quit mid-race — the server marks them out (non-terminating; the
 * race continues for everyone else) and the client returns to the lobby. Set
 * false to hide the option (e.g. classroom races where the coach controls the
 * session).
 */
export const SHOW_LEAVE_RACE = true;

// ── Classroom ───────────────────────────────────────────────────────────────
/** Students auto-join the moment they tap "Join Puzzle Royale". */
export const CLASSROOM_AUTO_JOIN = true;
/** Classroom royales NEVER auto-start — only the coach can start. */
export const CLASSROOM_AUTO_START = false;

// ── Defaults ────────────────────────────────────────────────────────────────
/**
 * The host-settings object a freshly-opened Create modal starts from. Topics
 * is intentionally empty — the host MUST pick at least one (PRD: no default,
 * but cannot start empty; the Create button stays disabled until non-empty).
 */
export const DEFAULT_ROYALE_SETTINGS: RoyaleSettings = {
  topics: [],
  timerSec: TIME_DEFAULT,
  difficulty: DIFFICULTY_DEFAULT,
  punish: PUNISH_DEFAULT,
  punishThreshold: PUNISH_DEFAULT_THRESHOLD,
  minPlayers: MIN_TO_START,
  maxPlayers: DISPLAY_CAP,
  autoStartAt: AUTO_START_AT,
  lobbyExpiryMs: LOBBY_EXPIRY_MS,
};
